<template>
  <div class="data-table mt-2">
    <div
      class="top-row align-items-center position-sticky"
      v-if="showPageCounter"
    >
      <div class="item-per-page">
        <span>{{ getLabelByKeys("DATA_TABLE.SHOW") }}</span>
        <div class="item-per-page-selection">
          <multiselect
            v-model="itemPerPage"
            :options="[10, 20, 50, 100, 2500, 'All']"
            :multiple="false"
            :searchable="true"
            :close-on-select="true"
            @open="onMultiselectOpen"
            @close="onMultiselectClose"
            :show-labels="false"
            :allow-empty="false"
            :placeholder="getLabelByKeys('PLACE_HOLDERS.NUMBER')"
            @input="currentPage = 1"
          ></multiselect>
        </div>
        <span> {{ getLabelByKeys("DATA_TABLE.ITEMS_PER_PAGE") }}</span>
      </div>
      <div class="flex-grow-1 pl-2">
        <slot name="inside-top"></slot>
      </div>
      <div class="page-info-container">
        <span>
          {{
            getLabelByKeys(
              "DATA_TABLE.SHOWING_RANGE",
              startNumber,
              endNumber,
              listAllRows.length
            )
          }}
        </span>
      </div>
      <slot name="inside-top-right"></slot>
    </div>
    <table class="table table-striped mt-2 border">
      <thead
        :class="{ 'table-sticky-header': hasStickyHeader }"
        style="
          background-color: var(--coredarkcolor);
          color: var(--corelightcolor);
        "
      >
        <tr class="" v-if="categoryHeaders.length > 0">
          <th v-if="LeftHeader2DTable" scope="col" class="table-header"></th>
          <th
            scope="col"
            class="table-header"
            v-for="(category, index) of categoryHeaders"
            :key="'categoryHeaders' + index"
            :colspan="category.span"
          >
            {{ category.displayLabel }}
          </th>
        </tr>
        <tr class="">
          <th scope="col" class="table-header" v-if="showCheckbox">
            <div class="form-group form-check">
              <input
                class="form-check-input"
                style=""
                type="checkbox"
                v-model="isSelectAll"
                @change="handleClickSelectAll($event.target.checked)"
              />
            </div>
          </th>
          <th
            v-for="(field, index) of mergedHeaders"
            :key="'header' + index"
            scope="col"
            class="table-header"
          >
            <span v-if="field.hideSort" class="max-content">
              {{ field.displayLabel_lang_English || field.displayLabel }}
              <span v-if="hasSumHeader" class="sum-info">{{
                getSum(field)
              }}</span>
            </span>
            <span v-else class="max-content">
              {{ field.displayLabel_lang_English || field.displayLabel }}
              <span v-if="hasSumHeader" class="sum-info">{{
                getSum(field)
              }}</span>
              <i
                class="material-icons sort-button"
                v-if="sortModel !== getSortModel(field)"
                @click="handleSortColumn(field, 1)"
              >
                unfold_more
              </i>
              <i
                class="material-icons sort-button"
                v-if="sortModel === getSortModel(field) && sortOrder === 1"
                @click="handleSortColumn(field, -1)"
              >
                arrow_drop_up
              </i>
              <i
                class="material-icons sort-button"
                v-if="sortModel === getSortModel(field) && sortOrder === -1"
                @click="handleSortColumn(field, 1)"
              >
                arrow_drop_down
              </i>
            </span>
          </th>
          <slot name="header"></slot>
        </tr>
      </thead>
      <tbody>
        <!-- <tr v-for="row in listData" :key="row.id" @click="showCandidateModal(row)" style="cursor: pointer;"> -->
        <tr
          v-for="(row, rowIndex) in displayData"
          :key="'row-' + row.id + '-index-' + rowIndex"
          :class="{
            newEntryRow: row.status == 'Applied',
            'redundant-builder': isRedundantBuilder(row),
            'in-used-builder': findUsingBuilder(row),
          }"
          :style="{ backgroundColor: getColor(row), color: getTextColor(row) }"
          style="cursor: pointer"
        >
          <template v-if="row.isGroupHeader">
            <td class="group-header pt-4 pl-4" :colspan="numberOfColumns + 3">
              <h4 v-if="!row.header">Unspecify</h4>
              <TableCellBadge
                v-else-if="['types', 'tags'].includes(row.model)"
                :content="row.header"
                class="badge-group-header"
                :name="row.model"
                :getColor="getColor"
              />
              <h4 v-else>{{ row.header }}</h4>
            </td>
          </template>
          <template v-else>
            <td @click.stop="" v-if="showCheckbox">
              <div class="form-group form-check">
                <input
                  class="form-check-input"
                  style=""
                  type="checkbox"
                  :value="row.id"
                  v-model="checkedRows"
                />
              </div>
            </td>
            <td
              v-for="(field, index) of mergedHeaders"
              :key="'cell' + row.id + '-' + index"
              :class="LeftHeader2DTable && index === 1 ? 'table-header' : ''"
              @click="$emit('clickRow', row)"
              class="max-content"
            >
              <slot
                v-if="field.model === 'custom-actions-slot'"
                name="custom-actions"
                :data="row"
              ></slot>
              <slot
                v-else-if="field.model === 'job-details-slot'"
                name="job-details"
                :data="row"
              ></slot>
              <slot
                v-else-if="field.model === 'custom-column-slot'"
                name="custom-column"
                :data="row"
              ></slot>
              <p v-else-if="field.displayCheckMark">
                <i class="material-icons" v-if="getCellContent(row, field)">
                  check_circle
                </i>
              </p>
              <p v-else-if="field.model === 'poster_name' && isShowUserDialog">
                <button
                  v-if="row[field.model]"
                  @click="
                    showUserProfileDialog(row['poster_id'] || row.poster, false)
                  "
                  class="mylisting-button dark-button"
                >
                  {{ row[field.model] }}
                </button>
              </p>
              <p v-else-if="['types', 'tags'].includes(field.model)">
                <TableCellBadge
                  :content="getCellContent(row, field)"
                  :name="field.model"
                  :getColor="getColor"
                />
              </p>
              <p v-else-if="field.model === 'files'">
                <a
                  v-for="(field, index) of row[field.model]"
                  :key="'header' + index"
                  :href="field"
                  class="d-block"
                  target="_blank"
                >
                  {{ field.split("==/")[1] }}
                </a>
              </p>
              <TableCell
                v-else
                :field="field"
                :row="row"
                :listStatusColors="listStatusColors"
                :isJobTable="isJobTable"
                :isApplicantTable="isApplicantTable"
                :isTemplateTable="isTemplateTable"
                :content="getCellContent(row, field)"
              />
            </td>
            <slot name="actions" :data="row"></slot>
          </template>
        </tr>
        <slot
          name="footerRow"
          :currentPage="currentPage"
          :totalPage="totalPage"
        ></slot>
      </tbody>
    </table>
    <div class="pagination-panel mb-3" v-if="showPageCounter">
      <div class="paging-container">
        <span>
          {{
            getLabelByKeys(
              "DATA_TABLE.SHOWING_RANGE",
              startNumber,
              endNumber,
              listAllRows.length
            )
          }}
        </span>
      </div>
      <ul class="pagination pagination-lg" v-if="itemPerPage !== 'All'">
        <li
          class="page-item"
          @click="handleClickPrevious()"
          v-if="currentPage > 1"
        >
          <a class="page-link" :aria-label="getLabelByKeys('BUTTONS.PREVIOUS')">
            <span>{{ getLabelByKeys("BUTTONS.PREVIOUS") }}</span>
          </a>
        </li>
        <li
          class="page-item"
          v-if="currentPage > 3 && currentPage === totalPage"
          @click="currentPage = currentPage - 3"
        >
          <a class="page-link">{{ currentPage - 3 }}</a>
        </li>
        <li
          class="page-item"
          v-if="currentPage > 2"
          @click="currentPage = currentPage - 2"
        >
          <a class="page-link">{{ currentPage - 2 }}</a>
        </li>
        <li
          class="page-item"
          v-if="currentPage > 1"
          @click="currentPage = currentPage - 1"
        >
          <a class="page-link">{{ currentPage - 1 }}</a>
        </li>
        <li class="page-item active">
          <a class="page-link">{{ currentPage }}</a>
        </li>
        <li class="page-item" @click="currentPage = currentPage + 1">
          <a class="page-link" v-if="currentPage < totalPage">
            {{ currentPage + 1 }}
          </a>
        </li>
        <li class="page-item" @click="currentPage = currentPage + 2">
          <a class="page-link" v-if="currentPage < totalPage - 1">
            {{ currentPage + 2 }}
          </a>
        </li>
        <li class="page-item" @click="currentPage = currentPage + 3">
          <a
            class="page-link"
            v-if="currentPage < totalPage - 2 && currentPage === 1"
          >
            {{ currentPage + 3 }}
          </a>
        </li>
        <li
          class="page-item"
          @click="handleClickNext()"
          v-if="currentPage < totalPage"
        >
          <a class="page-link" :aria-label="getLabelByKeys('BUTTONS.NEXT')">
            <span>{{ getLabelByKeys("BUTTONS.NEXT") }}</span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import TableCell from "./TableCell.vue";
import TableCellBadge from "./TableCellBadge.vue";

export default {
  name: "DataTable",
  props: {
    // Specify header of table. Each element include: displayLabel, model, category (optional)
    listHeaders: {
      type: Array,
      default: () => [],
    },
    //if extra headers need to be displayed,passed through this prop. Each element include: displayLabel, model, category (optional)
    additionalHeaders: {
      type: Array,
      default: () => [],
    },

    //Specify another row of headers above listHeaders to categorize listHeaders. Each element includes: displayLabel, categoryModel, span
    categoryHeaders: {
      type: Array,
      default: () => [],
    },
    //option to hide checklists on displayed data
    showCheckbox: {
      type: Boolean,
      default: () => true,
    },

    //Specify if the first cell in each row is a header cell
    LeftHeader2DTable: {
      type: String,
      default: () => "",
    },
    // Specify data of table. Data for each column is map by model value
    listData: {
      type: Array,
      default: () => [],
    },
    listFields: {
      type: Array,
      default: () => [],
    },
    listStatus: {
      type: Array,
      default: () => [],
    },
    // List of checked row
    value: {
      type: Array,
      default: () => [],
    },
    isCandidateTable: {
      type: Boolean,
      default: true,
    },
    isShiftJobTable: {
      type: Boolean,
      default: false,
    },
    // Sort from outside
    sortInfo: Object,
    defaultItemPerPage: {
      type: [Number, String],
      default: 50,
    },
    defaultSortInfo: Object,
    showPageCounter: {
      type: Boolean,
      default: true,
    },
    isJobTable: {
      type: Boolean,
      default: false,
    },
    isApplicantTable: {
      type: Boolean,
      default: false,
    },
    isTemplateTable: {
      type: Boolean,
      default: false,
    },
    isShowUserDialog: {
      type: Boolean,
      default: false,
    },
    groupBy: String,
    hasStickyHeader: {
      type: Boolean,
      default: false,
    },
    hasSumHeader: {
      type: Boolean,
      default: false,
    },
    noColor: {
      type: Boolean,
      default: false,
    },
    isRedundantBuilder: {
      type: Function,
      default: () => {},
    },
    findUsingBuilder: {
      type: Function,
      default: () => {},
    },
  },
  components: {
    TableCell,
    TableCellBadge,
  },
  data() {
    const listDateModel = [
      "date",
      "created_date",
      "last_login",
      "last_updated_date",
      "last_activity",
      "recent_access",
    ];
    const listTimeModel = [
      "start_time",
      "end_time",
      "actual_start_time",
      "actual_end_time",
    ];
    let sortModel;
    let sortOrder;
    if (this.defaultSortInfo) {
      sortModel = this.defaultSortInfo.model;
      sortOrder = this.defaultSortInfo.order;
    } else {
      // Load default sort
      if (this.isCandidateTable) {
        sortModel = this.$store.getters.agency.sort_candidates_by;
      } else {
        sortModel = this.$store.getters.agency.sort_clients_by;
      }
      if (!sortModel || sortModel.includes(" ")) {
        // Created date is default value
        sortModel = "created_date";
      }
      if (listDateModel.includes(sortModel)) {
        sortOrder = -1;
      } else {
        sortOrder = 1;
      }
    }
    let secondarySort = null;
    if (this.defaultSortInfo) {
      if (this.defaultSortInfo.secondary_model) {
        secondarySort = {
          model: this.defaultSortInfo.secondary_model,
          order: this.defaultSortInfo.secondary_order,
        };
      }
    }
    return {
      checkedRows: [],
      sortModel: sortModel,
      sortOrder: sortOrder,
      secondarySort: secondarySort,
      isSelectAll: false,
      listDateModel: listDateModel,
      listTimeModel: listTimeModel,
      numberOfLoadingRequest: 0, // Number of loading request when click check all
      itemPerPage: this.defaultItemPerPage,
      currentPage: 1,
    };
  },
  created() {
    this.$emit("sort", { model: this.sortModel, order: this.sortOrder });
  },
  watch: {
    sortInfo: {
      handler() {
        if (!this.sortInfo) {
          return;
        }
        if (!this.sortInfo.order || !this.sortInfo.model) {
          return;
        }
        this.sortOrder = this.sortInfo.order;
        this.sortModel = this.sortInfo.model;
      },
      immediate: true,
    },
    checkedRows: {
      handler() {
        if (
          this.displayData.length &&
          this.checkedRows.length === this.displayData.length
        ) {
          this.isSelectAll = true;
        } else {
          this.isSelectAll = false;
        }
        if (this.checkedRows.length) {
          const listCheckedRows = this.displayData.filter((item) =>
            this.checkedRows.includes(item.id)
          );
          this.$emit("input", listCheckedRows);
        } else {
          this.$emit("input", []);
        }
      },
      immediate: true,
    },
    // value: {
    //   handler() {
    //     if (this.value) {
    //       this.checkedRows = this.value.map((item) => item.id);
    //     } else {
    //       this.checkedRows = [];
    //     }
    //   },
    //   immediate: true,
    // },
    displayData: {
      handler() {
        this.updateCheckedRow();
        this.isSelectAll = false;
      },
      immediate: true,
    },
    listData: {
      handler() {
        // Check if all of the checked data is still in the screen
        this.updateCheckedRow();
        this.isSelectAll = false;
        this.currentPage = 1;
      },
      immediate: true,
    },
  },
  updated() {
    // After updated, hide loading (loading is showed when click select all)
    if (this.numberOfLoadingRequest === 1) {
      this.numberOfLoadingRequest -= 1;
      this.hideLoading();
    }
  },
  computed: {
    ...mapGetters([
      "agency",
      "clientTableTypesColorList",
      "clientTableTagsColorList",
      "candidateTableTypesColorList",
      "candidateTableTagsColorList",
      "agencyCurrencySign",
    ]),
    numberOfColumns() {
      let result = this.mergedHeaders.length + 1;
      if (this.showCheckbox) {
        result += 1;
      }
      return result;
    },
    listStatusColors() {
      if (!this.$store.getters.agency) {
        return [];
      }
      if (this.isCandidateTable) {
        const listStatusColors =
          this.$store.getters.agency.candidate_table_status_color;
        return listStatusColors ? listStatusColors : [];
      } else if (this.isShiftJobTable) {
        const listStatusColors = this.$store.getters.shiftJobStatuses;
        return listStatusColors ? listStatusColors : [];
      } else if (this.isApplicantTable) {
        const listStatusColors =
          this.$store.getters.agency.public_advance_setting
            .candidate_table_application_status_color;
        return listStatusColors ? listStatusColors : [];
      } else {
        const listStatusColors =
          this.$store.getters.agency.client_table_status_color;
        return listStatusColors ? listStatusColors : [];
      }
    },
    listTypesColors() {
      if (!this.agency) {
        return [];
      }
      if (
        this.isCandidateTable &&
        this.tryGet(() => this.candidateTableTypesColorList)
      ) {
        return this.candidateTableTypesColorList;
      }
      if (
        !this.isCandidateTable &&
        this.tryGet(() => this.clientTableTypesColorList)
      ) {
        return this.clientTableTypesColorList;
      }
      return [];
    },
    listTagsColors() {
      if (!this.agency) {
        return [];
      }
      if (
        this.isCandidateTable &&
        this.tryGet(() => this.candidateTableTagsColorList)
      ) {
        return this.candidateTableTagsColorList;
      }
      if (
        !this.isCandidateTable &&
        this.tryGet(() => this.clientTableTagsColorList)
      ) {
        return this.clientTableTagsColorList;
      }
      return [];
    },
    mergedHeaders() {
      let headerArray = [];
      headerArray = this.listHeaders.concat(this.additionalHeaders);
      if (this.LeftHeader2DTable) {
        headerArray.unshift({
          displayLabel: this.LeftHeader2DTable,
          model: "LeftHeader",
        });
      }

      // remove 'adminNotes' column from table header as we are passing it through slot.
      headerArray = headerArray.filter(
        (header) => header.model !== "adminNotes"
      );
      //return new array containing basic headers and the addition headers
      return headerArray;
    },
    listAllRows() {
      let listData = [...this.listData];
      if (this.groupBy) {
        const mapGroup = listData.reduce((acc, cur) => {
          acc[cur[this.groupBy]] = acc[cur[this.groupBy]] || [];
          acc[cur[this.groupBy]].push(cur);
          return acc;
        }, Object.create(null));
        let listKeys = Object.keys(mapGroup);
        if (this.sortModel === this.groupBy) {
          listKeys = listKeys.sort(this.sortStatus);
        } else {
          listKeys = listKeys.sort((e1, e2) => e1 > e2);
        }
        let result = [];
        for (const key of listKeys) {
          let listValues = mapGroup[key];
          result.push({
            isGroupHeader: true,
            header: key,
            model: this.groupBy,
          });
          listValues = listValues.sort(this.sortRows);
          result = [...result, ...listValues];
        }
        return result;
      } else {
        // Sort data before display
        if (this.sortModel && this.sortOrder) {
          listData = listData.sort(this.sortRows);
        }
      }
      return listData;
    },
    displayData() {
      if (this.itemPerPage === "All") {
        return this.listAllRows;
      }

      return this.listAllRows.slice(
        (this.currentPage - 1) * this.itemPerPage,
        this.currentPage * this.itemPerPage
      );
    },
    totalPage() {
      if (this.itemPerPage === "All") {
        return 1;
      }
      return Math.ceil(this.listAllRows.length / this.itemPerPage);
    },
    startNumber() {
      if (this.itemPerPage === "All") {
        return 1;
      }
      return (this.currentPage - 1) * this.itemPerPage + 1;
    },
    endNumber() {
      if (this.itemPerPage === "All") {
        return this.listAllRows.length;
      }
      let endNumber = this.currentPage * this.itemPerPage;
      if (endNumber < this.listAllRows.length) {
        return endNumber;
      }
      return this.listAllRows.length;
    },
  },
  methods: {
    getSortModel(field) {
      return field.sort_model || field.model;
    },
    sortStatus(status1, status2) {
      // Find index in list status
      const index1 = this.listStatus.indexOf(status1);
      const index2 = this.listStatus.indexOf(status2);
      // If both status are not in list status, sort by alphabet
      if (index1 === -1 && index2 === -1) {
        if (status1 == null) {
          return 0 - this.sortOrder;
        }
        if (status2 == null) {
          return this.sortOrder;
        }
        return status1.toLowerCase() < status2.toLowerCase()
          ? 0 - this.sortOrder
          : this.sortOrder;
      }

      return index1 < index2 ? 0 - this.sortOrder : this.sortOrder;
    },
    sortRows(e1, e2) {
      if (this.sortModel === "status" && this.listStatus) {
        return this.sortStatus(e1.status, e2.status);
      } else if (this.sortModel === "first_name_last_name") {
        const firstName1 = e1["first_name"];
        const lastName1 = e1["last_name"];
        const firstName2 = e2["first_name"];
        const lastName2 = e2["last_name"];
        if (firstName1 == null || firstName2 == null) {
          // Compare last name
          if (lastName1 == null || lastName2 == null) {
            return this.sortOrder;
          } else {
            return lastName1.toLowerCase() < lastName2.toLowerCase()
              ? 0 - this.sortOrder
              : this.sortOrder;
          }
        } else {
          if (firstName1.toLowerCase() === firstName2.toLowerCase()) {
            // Compare last name
            return lastName1.toLowerCase() < lastName2.toLowerCase()
              ? 0 - this.sortOrder
              : this.sortOrder;
          } else {
            // Compare first name
            return firstName1.toLowerCase() < firstName2.toLowerCase()
              ? 0 - this.sortOrder
              : this.sortOrder;
          }
        }
      } else if (this.sortModel === "recent_access") {
        return this.getLastAccessTime(e1) < this.getLastAccessTime(e2)
          ? 0 - this.sortOrder
          : this.sortOrder;
      } else if (
        ["timestamp", "created_date", "date"].includes(this.sortModel)
      ) {
        if (this.secondarySort) {
          if (e1[this.sortModel] === e2[this.sortModel]) {
            const sortOrder = this.secondarySort.order || 1;
            return e1[this.secondarySort.model] < e2[this.secondarySort.model]
              ? 0 - sortOrder
              : sortOrder;
          }
        }
        return e1[this.sortModel] < e2[this.sortModel]
          ? 0 - this.sortOrder
          : this.sortOrder;
      } else if (["application_date", "report_date"].includes(this.sortModel)) {
        if (e1[this.sortModel] && e2[this.sortModel]) {
          return new Date(e1[this.sortModel].replace("at ", "")) >
            new Date(e2[this.sortModel].replace("at ", ""))
            ? 0 - this.sortOrder
            : this.sortOrder;
        }
      } else {
        if (["distance_to_job"].includes(this.sortModel)) {
          if (e1[this.sortModel] == null) {
            return this.sortOrder;
          }
          if (e2[this.sortModel] == null) {
            return 0 - this.sortOrder;
          }
        } else {
          if (e1[this.sortModel] == null) {
            return 0 - this.sortOrder;
          }
          if (e2[this.sortModel] == null) {
            return this.sortOrder;
          }
        }
        // Numeric fields
        if (
          typeof e1[this.sortModel] === "number" &&
          typeof e2[this.sortModel] === "number"
        ) {
          // Numeric fields
          return e1[this.sortModel] < e2[this.sortModel]
            ? 0 - this.sortOrder
            : this.sortOrder;
        }
        return e1[this.sortModel].toString().toLowerCase() <
          e2[this.sortModel].toString().toLowerCase()
          ? 0 - this.sortOrder
          : this.sortOrder;
      }
    },
    getColor(item, fieldName) {
      if (this.noColor) return "";
      if (fieldName) {
        if (fieldName === "types" && item) {
          const typeColor = this.listTypesColors.find(
            (listItem) => listItem.type && listItem.type.trim() === item.trim()
          );
          if (typeColor) {
            return typeColor.color;
          }
          return "#d9d9da"; //this is the default color for type pill
        }
        if (fieldName === "tags" && item) {
          const tagColor = this.listTagsColors.find(
            (listItem) => listItem.tag && listItem.tag.trim() === item.trim()
          );
          if (tagColor) {
            return tagColor.color;
          }
          return "#d9d9da"; //this is the default color for tag pill
        }
        return;
      }
      const statusColor = this.listStatusColors.find(
        (statusColor) => statusColor.status === item.status
      );
      if (statusColor) {
        return statusColor.color;
      }
    },
    getTextColor(item) {
      if (this.isTemplateTable && item.deactivated) {
        return "#cccccc";
      }
      return "black";
    },
    handleClickSelectAll(isSelectAll) {
      // When click select all, show loading
      if (this.numberOfLoadingRequest === 0) {
        this.numberOfLoadingRequest += 1;
        this.showLoading();
      }
      setTimeout(() => {
        if (isSelectAll) {
          // Click select all at candidate tab
          this.checkedRows = this.displayData.map((item) => item.id);
        } else {
          // Deselect all
          this.checkedRows = [];
        }
      }, 10);
    },
    handleSortColumn(field, order) {
      this.sortModel = field.sort_model || field.model;
      this.sortOrder = order;
      this.$emit("sort", { model: field.model, order: order });
    },
    getCellContent(row, field) {
      if (field.transformFunction) {
        const content = field.transformFunction(row);
        if (!field.model) {
          field.model = field.displayLabel || field.label;
        }
        if (field.model.indexOf(this._uid + "_") !== 0) {
          field.model = this._uid + "_" + field.model;
        }
        row[field.model] = content;
        return content;
      }
      if (field.transformModelFunction) {
        return field.transformModelFunction(row[field.model]);
      }
      if (this.categoryHeaders.length > 0 && field.hasOwnProperty("category")) {
        return row[field.category][field.model];
      }
      return this.getTableCellContent(row, field, this.listFields);
    },
    handleClickPrevious() {
      if (this.currentPage > 1) {
        this.currentPage = this.currentPage - 1;
      }
    },
    handleClickNext() {
      if (this.currentPage < this.totalPage) {
        this.currentPage = this.currentPage + 1;
      }
    },
    getSum(field) {
      let result = 0;
      let isAllEmpty = true;
      for (const item of this.listAllRows) {
        if (item[field.model] != null && item[field.model] !== "") {
          isAllEmpty = false;
        }
        if (isNaN(item[field.model])) {
          return "";
        }
        result += Number(item[field.model]);
      }
      if (isAllEmpty) {
        return "";
      }
      return result;
    },
    // Check if all of the checked data is still in the screen
    updateCheckedRow() {
      if (!this.checkedRows || !this.displayData) {
        this.checkedRows = [];
        return;
      }
      const listIds = this.checkedRows;
      const mapCurrentDisplayIds = this.displayData.map((item) => item.id);
      if (!listIds.every((id) => mapCurrentDisplayIds.includes(id))) {
        this.checkedRows = [];
        return;
      }
    },
  },
};
</script>

<style scoped>
.pagination-panel .pagination .page-item:not(.active) .page-link {
  color: var(--coredarkcolor);
}

.pagination-panel .pagination .page-item.active .page-link {
  background-color: var(--coredarkcolor);
  border-color: var(--coredarkcolor);
}

.pagination-panel {
  display: flex;
  flex-wrap: wrap;
}

.page-info-container {
  display: flex;
  align-items: center;
  justify-content: end;
}

.paging-container {
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: start;
}

.top-row {
  display: flex;
}

.client-table .top-row,
.candidate-table .top-row {
  padding-top: 10px;
}

.item-per-page {
  display: flex;
  align-items: center;
}

.item-per-page-selection {
  margin-left: 0.5em;
  margin-right: 0.5em;
}
.pagination-lg .page-link {
  font-size: 16px;
  padding: 8px 16px;
}

.page-item:not(.active) {
  cursor: pointer;
}

.cell-content {
  white-space: pre-wrap;
}

.break-all {
  word-break: break-all;
}

.change-icon {
  color: rgb(209, 181, 21);
}

.group-header {
  text-align: left;
  background-color: var(--coredarkcolor);
  color: var(--corelightcolor);
}

.group-header > h4 {
  font-weight: bold;
}

.badge-group-header {
  font-size: 1.5em;
}

.sort-button {
  cursor: pointer;
}

.table-sticky-header {
  position: sticky;
  top: 0px;
  z-index: 1;
}
.multiselect--active {
  z-index: 3;
}

.view-applicants-table .table td {
  vertical-align: middle !important;
}
/* .max-content {
  width: max-content;
} */

.sum-info {
  margin-left: 0.5em;
  font-size: 0.9em;
  font-weight: 400;
}

.redundant-builder > td:not(.action-column) {
  opacity: 0.3;
}

.redundant-builder .edit-form-icon {
  opacity: 0.3;
}

.in-used-builder .delete-icon {
  opacity: 0.3;
}
</style>


// WEBPACK FOOTER //
// src/components/common/DataTable.vue